/**
 * Viewport positioning service
 * Handles all viewport-related calculations and positioning logic
 */

const VIEWPORT_THRESHOLDS = Object.freeze({
    LEFT: 0.25,
    RIGHT: 0.75,
    TOP: 0.25,
    BOTTOM: 0.75,
  });
  
  const POSITION_OFFSETS = Object.freeze({
    HORIZONTAL: 0.008,
    VERTICAL: 0.004,
  });
  
  export class ViewportService {
    /**
     * Check if element is on specific side of viewport
     * @param {string} side - 'left', 'right', 'top', 'bottom'
     * @param {DOMRect} rect - Element bounding rect
     * @param {number} viewportWidth - Viewport width
     * @param {number} viewportHeight - Viewport height
     * @returns {boolean}
     */
    static isPositionSide(side, rect, viewportWidth, viewportHeight) {
      if (!rect) return false;
      
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
  
      switch (side) {
        case 'left':
          return centerX < (viewportWidth * VIEWPORT_THRESHOLDS.LEFT);
        case 'right':
          return centerX > (viewportWidth * VIEWPORT_THRESHOLDS.RIGHT);
        case 'top':
          return centerY < (viewportHeight * VIEWPORT_THRESHOLDS.TOP);
        case 'bottom':
          return centerY > (viewportHeight * VIEWPORT_THRESHOLDS.BOTTOM);
        default:
          return false;
      }
    }
  
    /**
     * Calculate adjusted position when hiding element
     * @param {number} x - Original X position
     * @param {number} y - Original Y position
     * @param {DOMRect} rect - Element bounding rect
     * @param {number} viewportWidth - Viewport width
     * @param {number} viewportHeight - Viewport height
     * @param {boolean} show - Whether element should be visible
     * @returns {object} Adjusted coordinates
     */
    static calculatePosition(x, y, rect, viewportWidth, viewportHeight, show = true) {
      if (show) {
        return { x, y };
      }
  
      let adjustedX = x;
      let adjustedY = y;
  
      // Horizontal adjustment
      if (this.isPositionSide('left', rect, viewportWidth, viewportHeight)) {
        adjustedX = x - POSITION_OFFSETS.HORIZONTAL;
      } else if (this.isPositionSide('right', rect, viewportWidth, viewportHeight)) {
        adjustedX = x + POSITION_OFFSETS.HORIZONTAL;
      }
  
      // Vertical adjustment  
      if (this.isPositionSide('top', rect, viewportWidth, viewportHeight)) {
        adjustedY = y - POSITION_OFFSETS.VERTICAL;
      } else if (this.isPositionSide('bottom', rect, viewportWidth, viewportHeight)) {
        adjustedY = y + POSITION_OFFSETS.VERTICAL;
      }
  
      return { x: adjustedX, y: adjustedY };
    }
  
    /**
     * Calculate animation delay based on position
     * @param {DOMRect} rect - Element bounding rect
     * @param {number} viewportWidth - Viewport width
     * @param {boolean} show - Whether element is showing
     * @returns {string} Delay in milliseconds
     */
    static calculateAnimationDelay(rect, viewportWidth, show = true) {
      if (!rect || !viewportWidth) return "0ms";
      
      const delay = (rect.left * 100) / viewportWidth;
      return `${show ? delay : delay / 2}ms`;
    }
  
    /**
     * Generate CSS positioning styles
     * @param {number} x - X coordinate (0-1)
     * @param {number} y - Y coordinate (0-1)
     * @param {string} delay - Animation delay
     * @returns {object} CSS style object
     */
    static getPositionStyles(x, y, delay = "0ms") {
      return {
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transitionDelay: delay,
        zIndex: Math.floor(y * 100),
      };
    }
  }